// kfe-login.js — real login screen (Brief 188; rebuilt to the xib, Brief 121
// amendment, Chris's ruling of 2026-11-03).
//
// PORT TARGET: iOS KFELoginViewController.xib + .m. Brief 188 shipped the
// web's own design — a translucent panel, grey labelled fields, a solid
// orange submit and a Back button — and Brief 121 then put it on the
// LoginBackground artwork, which made the divergence from the phone obvious.
// Chris ruled on 2026-11-03: mirror the iPhone. So this screen is now the
// xib, control for control (Rule #9):
//
//   * no panel; two black boxes with white centred text, placeholder only
//   * "Log In" with iOS's flat orange fill under a white 1pt border
//   * "Forgot Password?" right-aligned beneath it
//   * "No licence? Click to buy." and "多了解功夫英语 请在此点击" pinned
//     near the bottom, both opening in a new tab
//   * no Back button — the xib has none, and there is nowhere to go back to
//
// Behaviour is untouched: Brief 188's submit path, Brief 194's expiry notice
// and ?return= handling, Brief 121's cold-boot route all work as before.
//
// Flow: form → crmWebLogin() → authStore.setSession() → router.navigate().
// crm-client.js stays DOM-free (Rule #1); this screen is the one place that
// turns its CrmResult into UI state and a route change.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import * as router from "../router.js";
import { crmWebLogin, crmCheckUsername, firstFieldError, hasNoLicense } from "../data/crm-client.js";
import {
  setSession,
  takeSessionExpiredNotice,
  getLastUsername,
  setLastUsername,
} from "../data/auth-store.js";
import { setForgotHandoff, takeReturningUsername } from "../data/forgot-handoff.js";
import { setTrialContextFromLogin } from "../data/trial-store.js";
import { ASSET_BASE } from "../ui/kfe-splash.js";

/**
 * iOS's "No licence? Click to buy." presents the in-app StoreKit subscription
 * screen (KFELoginViewController.m clickRegister:), which a browser cannot do.
 * Chris's substitute, ruled 2026-11-03: open the web store in a new tab.
 */
export const BUY_URL = "https://store.kungfuenglish.com/buy";

/** urlButtonAction: opens exactly this (KFELoginViewController.m:230). */
export const LEARN_MORE_URL = "https://www.kungfuenglish.com";

export class KFELogin extends KFEComponent {
  static styles = `
    /* GEOMETRY IS THE XIB'S (Brief 121 amendment, Chris 2026-11-03).
       KFELoginViewController.xib is laid out on a 375x667 canvas in points,
       and every number below is lifted from it unchanged — CSS px and iOS
       points are the same unit on a phone, so the port is literal:

         fields / buttons   53pt side insets, 269x40 (375 - 2x53 = 269)
         the stack          password centred on centerY - 44, 15pt gaps
         Forgot Password?   122x24, trailing-aligned with the Log In button
         bottom pair        10pt apart, lower one 50pt off the bottom edge

       The absolute positioning is not a stylistic choice — it is what the
       xib's constraints say, and a flow layout could not hold the stack at
       "centre minus 44" while pinning the pair to the bottom. */
    :host {
      position: fixed; inset: 0;
      display: block;
      color: #fff;
      font-family: var(--kfe-font);
      /* iOS draws this screen in IdealSans, which the web build does not
         licence or vendor. Sizes and weights below are the xib's; the face
         is the app's own stack. The one unavoidable divergence (Rule #9). */
    }

    /* LoginBackground, pinned to all four edges of the root view. iOS uses
       the SAME artwork behind the launch screen and the login, so the
       learner's first transition is logo -> form on one unbroken backdrop. */
    .backdrop {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover; object-position: center;
      pointer-events: none;
    }

    /* iOS is portrait-phone only, so the xib has no opinion about a 1400px
       window. Rather than stretch 53pt insets across a desktop viewport,
       the screen keeps Brief 198's app column (--kfe-app-max-width) and
       lays the xib out inside it. At or below 480px — every phone — this
       container is the full viewport and the geometry is byte-identical. */
    .screen {
      position: absolute; inset: 0;
      max-width: var(--kfe-app-max-width, 480px);
      margin-inline: auto;
    }

    .stack {
      position: absolute;
      left: 53px; right: 53px;
      /* username top = centreY - 44 - 20 (half a field) - 55 (a field + gap).
         On the xib's 667pt canvas that is 333.5 - 119 = 214.5 — its frame. */
      top: calc(50% - 119px);
    }

    /* Black box, white centred text. borderStyle=roundedRect draws nothing
       once backgroundColor is opaque, which is why the phone shows square
       corners — see docs/121-proof/ios-login.jpg. */
    .stack input {
      display: block; box-sizing: border-box;
      width: 100%; height: 40px; margin: 0 0 15px;
      border: 0; border-radius: 0;
      background: #000; color: #fff;
      font-family: inherit; font-size: 17px; font-weight: 600;
      text-align: center;
    }
    /* attributedPlaceholder, UIColor colorWithWhite:.8 (KFELoginViewController.m). */
    .stack input::placeholder { color: rgba(255, 255, 255, 0.8); }
    .stack input:focus { outline: 2px solid #fff; outline-offset: -2px; }
    .stack input[disabled] { opacity: 0.6; }

    /* The flat orange fill is iOS's own (xib backgroundColor #FF931E) under
       the white 1pt border viewDidLoad adds. It reads as an outline because
       the fill matches the artwork's base orange — but it IS a fill: the
       backdrop's rays stop at the border on the phone, and a transparent
       button would let them through. cornerRadius 4. */
    button.primary {
      display: block; box-sizing: border-box;
      width: 100%; height: 40px; margin: 0 0 15px;
      border: 1px solid #fff; border-radius: 4px;
      background: #FF931E; color: #fff;
      font-family: inherit; font-size: 17px; font-weight: 600;
      cursor: pointer;
    }
    button.primary[disabled] { opacity: 0.5; cursor: not-allowed; }

    /* 122x24 with 4pt of vertical content inset, trailing-aligned with the
       Log In button. The xib's 122pt is the width IdealSans-Medium 16 needs
       for "Forgot Password?"; the substitute face needs more and would wrap
       it onto two lines, which iOS never does (lineBreakMode is a single-line
       truncation). The invariant the xib is expressing is the TRAILING
       alignment, so the box keeps 122pt as a floor and grows leftwards from
       that edge instead. */
    a.forgot {
      display: block; box-sizing: border-box;
      width: max-content; min-width: 122px;
      margin-left: auto; padding: 4px 0;
      color: #fff; text-decoration: none; text-align: center;
      white-space: nowrap;
      font-size: 16px; font-weight: 500; line-height: 16px;
    }

    /* The slot iOS gives additionalTextLabel: 30pt below Forgot Password?,
       15pt wider than the button column on each side. iOS raises login
       errors in a modal KFEAlertView, which Brief 188 deliberately did not
       port; the message stays in the page, and this is where the page has
       room for it. Black box so white text stays legible on the artwork. */
    .message {
      margin: 30px -15px 0; padding: 0.5rem 0.75rem;
      background: rgba(0, 0, 0, 0.85);
      font-size: 0.85rem; line-height: 1.35;
      color: #fff;
    }

    /* The store link inside the error message (Brief 016 criterion 3). Plain
       underlined text rather than a second orange button: the bottom pair
       already carries one, and iOS's equivalent is a button on an alert, not
       a control added to this screen. */
    .message a.buy-link {
      display: inline-block; margin-top: 0.35rem;
      color: #fff; text-decoration: underline;
      font-weight: 600;
    }

    .bottom {
      position: absolute;
      left: 53px; right: 53px; bottom: 50px;
      display: flex; flex-direction: column; gap: 10px;
    }
    /* KFERegisterButton: white title, 1pt white border, KFEButton's
       cornerRadius 5 and IdealSans-Medium 17, 14pt horizontal content inset.
       Same flat-fill story as Log In (#FFA426 here). */
    .bottom a {
      display: flex; align-items: center; justify-content: center;
      box-sizing: border-box;
      height: 40px; padding: 0 14px;
      border: 1px solid #fff; border-radius: 5px;
      background: #FFA426; color: #fff;
      text-decoration: none; white-space: nowrap;
      font-weight: 500;
      /* KFEButton sets adjustsFontSizeToFitWidth. 4.54vw hits 17px at the
         xib's 375pt width and shrinks below it, exactly where iOS would. */
      font-size: min(17px, 4.54vw);
    }
  `;

  /**
   * The username box starts filled, from one of two sources in this order:
   *
   *   1. the username the learner was working with in the forgot-password
   *      flow, if they have just come back from it (Brief 200 criterion 4).
   *      iOS needs nothing for this — its forgot screen is a MODAL over a
   *      login screen that was never torn down — but the web navigates, so
   *      the value is handed back through a one-shot slot. It wins, because
   *      it is this learner, a moment ago, as against whoever used the device
   *      last.
   *   2. the last username that signed in SUCCESSFULLY on this device, which
   *      is `KFELoginViewController.viewDidLoad`'s
   *      `userNameTF.text = [KFELoginManager getLastUserName]` (line 72) —
   *      Brief 200 item 4.
   *
   * A field initialiser rather than onConnected, so the first render already
   * carries it and the box never flashes empty.
   *
   * The PASSWORD box is left empty, as it is on iOS. Nothing persists a
   * password anywhere in this app, so there is no store to pre-fill it from
   * even by accident.
   * @type {string}
   */
  _username = takeReturningUsername() || getLastUsername();
  /** @type {string} */ _password = "";
  /** @type {boolean} */ _submitting = false;
  /** @type {string} */ _error = "";
  /**
   * Brief 016 criterion 3: show the store link beside the error, rather than
   * leaving a learner whose licence has run out on a message with nothing to
   * do. Set for an expired licence and for an expired trial — see
   * `_onSubmit`'s `expired_license` arm.
   * @type {boolean}
   */
  _showBuyLink = false;
  /**
   * Brief 194: true when we arrived here because the token expired, rather
   * than because the learner signed out or was displaced. Read-and-cleared
   * once, in onConnected — so a reload of /login does not keep re-announcing
   * it to someone who is already typing.
   * @type {boolean}
   */
  _sessionExpired = false;

  onConnected() {
    // KFEComponent renders BEFORE onConnected (component.js:69-74), so taking
    // the notice here needs a re-render to show it. Reading it on CONNECT
    // rather than in the field initialiser keeps the one-shot tied to actually
    // mounting the screen — an element that is constructed and discarded must
    // not swallow the notice.
    this._sessionExpired = takeSessionExpiredNotice();
    if (this._sessionExpired) this._scheduleRender();

    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: t("login.title") },
      bubbles: true,
      composed: true,
    }));

    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));
  }

  /** @param {Event} e */
  _onUsernameInput = (e) => {
    this._username = /** @type {HTMLInputElement} */ (e.target).value;
  };

  /** @param {Event} e */
  _onPasswordInput = (e) => {
    this._password = /** @type {HTMLInputElement} */ (e.target).value;
  };

  /** @returns {string | null} */
  _returnPath() {
    const raw = new URLSearchParams(location.search).get("return");
    return raw ? decodeURIComponent(raw) : null;
  }

  /**
   * `clickForgotPassword:` (KFELoginViewController.m:287-308), mirrored step
   * for step — Brief 200 item 3:
   *
   *   1. empty username  -> "Please enter your username", and NOTHING else
   *      happens (iOS shows it in a KFEAlertView and does not navigate);
   *   2. otherwise       -> GET check-username, ONCE;
   *   3. on success      -> the forgot-password screen, carrying `username`
   *      and `has_phone` (iOS sets both as properties on the VC it presents);
   *   4. on failure      -> the server's message, here on the login screen,
   *      and no navigation.
   *
   * The link keeps its real `href`, so a middle-click or a keyboard "open in
   * new tab" still reaches the screen (where the username box stage picks the
   * learner up). Only the plain left-click is intercepted.
   *
   * @param {Event} e
   */
  _onForgotPassword = async (e) => {
    const me = /** @type {MouseEvent} */ (e);
    // Let the browser handle anything that is not a plain primary click.
    if (me.metaKey || me.ctrlKey || me.shiftKey || me.altKey || me.button > 0) return;
    e.preventDefault();
    if (this._submitting) return;

    const username = this._username.trim();
    if (!username) {
      this._error = t("login.enterUsername");
      this._scheduleRender();
      return;
    }

    this._submitting = true;
    this._error = "";
    this._scheduleRender();

    const result = await crmCheckUsername({ username });
    if (!this.isConnected) return;
    this._submitting = false;

    if (result.success) {
      setForgotHandoff({ username, hasPhone: result.data?.has_phone === true });
      router.navigate(`/forgot-password?username=${encodeURIComponent(username)}`);
      return;
    }

    // iOS shows `json.errors![0].message[0]` for this call specifically
    // (KFEApiManager.swift:166) — the per-field sentence, not the envelope's
    // generic "Invalid input." Falls back through the envelope message to our
    // own generic copy.
    this._error = firstFieldError(result.error) || result.error?.message || t("login.errorGeneric");
    this._scheduleRender();
  };

  /** @param {Event} e */
  _onSubmit = async (e) => {
    e.preventDefault();
    if (this._submitting) return;
    const username = this._username.trim();
    const password = this._password;
    if (!username || !password) return;

    this._submitting = true;
    this._error = "";
    this._showBuyLink = false;
    this._scheduleRender();

    const result = await crmWebLogin({ username, password });

    this._submitting = false;

    if (result.success) {
      setSession(result.data);
      // Brief 016: iOS records the 7-day trial state on the LOGIN path, not
      // only on the trial screen — `KFELoginManager.analysisWithisSuccess`
      // calls `KFE7DaysTrialMgr.setIs7DaysTrialWithServerMessage` plus
      // `appDidActivate` for every successful login (KFELoginManager.m:188-192).
      // That is what makes the "ends in N days" message appear on a later
      // sign-in, and what clears the context when a learner who has bought
      // the course signs in as a full member. `result.raw` is the server's
      // own snake_case `data` block — see crm-client's CrmResult docblock.
      setTrialContextFromLogin(result.raw);
      // Brief 200 item 4: remember the username for the next visit, on
      // SUCCESS only — iOS writes its `kUsername` progress-settings row at
      // login success too, so a typo that failed to authenticate is never
      // what greets the learner next time.
      setLastUsername(username);
      // Brief 025: announce the signed-IN transition. `kfe:auth-changed` was
      // previously dispatched only on sign-OUT (auth-store.signOut), so the app
      // shell had no signal to start the sync engine on. Dispatch AFTER
      // setSession so every listener sees a live session, and BEFORE navigate so
      // the login pull is already in flight while the pathway screen mounts.
      document.dispatchEvent(new Event("kfe:auth-changed"));
      router.navigate(this._returnPath() ?? "/");
      return;
    }

    // Brief 016 criterion 1, and iOS's own first branch on a failed login:
    // `BOOL no_license = [[data objectForKey:@"no_license"] boolValue];` and,
    // when it is set, `[self showFreeTrial]` INSTEAD of any error alert
    // (KFELoginViewController.m:249-256). The learner is not being told they
    // got something wrong — they are being offered the free trial. So this
    // comes before the copy map, and nothing below it runs.
    //
    // The username travels in `?username=`, exactly as Brief 200 hands one to
    // /forgot-password, because iOS hands it over directly
    // (`vc.username = self.loginManager.userName`). The typed PASSWORD is not
    // carried: iOS's trial screen asks for it again ("Please re-input your
    // Password to begin"), and this app persists no password anywhere.
    if (hasNoLicense(result.error)) {
      router.navigate(`/free-trial?username=${encodeURIComponent(username)}`);
      return;
    }

    // Error-kind copy map. bad_credentials keeps its dedicated i18n key
    // (already localized to zh-Hans, matches iOS canon "用户名或密码不正确。").
    // For account_locked / rate_limited / validation_error / progress_conflict
    // the server's `message` field is Allen's canonical error copy (server
    // localizes based on request context) — prefer it over inventing
    // translations. English fallbacks kick in only if the server omits `message`.
    // network / generic / session_displaced (rare at login time) fall back
    // to the generic error and the learner stays on the screen.
    const errKind = result.error?.kind;
    const serverMsg = result.error?.message;
    if (errKind === "bad_credentials") {
      this._error = t("login.errorBadCredentials");
    } else if (errKind === "account_locked") {
      this._error = serverMsg || "Your account is locked. Please contact support.";
    } else if (errKind === "rate_limited") {
      this._error = serverMsg || "Too many attempts. Please wait a moment and try again.";
    } else if (errKind === "expired_license") {
      // 400 + 91012. iOS shows the server's own sentence in a KFEAlertView
      // titled "Login Error." and, when that sentence contains
      // "Trial has expired", attaches the Subscribe screen to the OK button
      // (KFELoginViewController.m:262-271 -> tag 10044 -> SubscribeViewController).
      // The web reaches the same outcome from the ERROR CODE rather than from
      // a substring of an English sentence the server localises — a more
      // robust mechanism for the same behaviour, which matters because this
      // message is shown in Chinese to most learners. The substring is kept
      // as a belt-and-braces fallback below, in case the CRM answers an
      // expired trial with some other code; whether it uses 91012 for the
      // trial as well as the paid licence is a question for Allen
      // (docs/016-handover.md).
      //
      // The store page is iOS's in-app StoreKit sheet's web substitute —
      // Chris's ruling of 2026-11-03, already the target of this screen's own
      // "No licence? Click to buy." button (Brief 121).
      // Read the RAW server message off the preserved body, not
      // `error.message` — crm-client fills that with an English default when
      // the server omits one, which would shadow the localised key below.
      // Exactly the trap Brief 188's Addendum 2 records for progress_conflict,
      // hit again here; the fix is the same one.
      this._error = result.error?.data?.message || t("login.errorExpiredLicense");
      this._showBuyLink = true;
    } else if (errKind === "validation_error") {
      this._error = serverMsg || t("login.errorGeneric");
      // iOS's own test: `if ([message containsString:@"Trial has expired"])`.
      this._showBuyLink = typeof serverMsg === "string" && serverMsg.includes("Trial has expired");
    } else if (errKind === "progress_conflict") {
      // 91048 — the CRM refuses login because another device has unsynced
      // progress. Full three-option resolution UI (wait / accept_loss /
      // reconcile) lives in a follow-up brief; here we surface the server
      // message + previous_device hint so the learner knows what's wrong and
      // what to try, instead of a generic error.
      //
      // Two things to know about the wire shape:
      //   1. The server's 91048 body wraps the conflict info in a top-level
      //      `data` field: { message, error_code, data: { previous_device,
      //      options, ... } }. crm-client.js preserves the WHOLE body as
      //      error.data, so previous_device lives at error.data.data.previous_device
      //      (the doubled "data" reflects wrapper + inner-payload nesting).
      //   2. Read the raw server message from error.data.message (top-level
      //      of the preserved body) rather than error.message — crm-client.js
      //      sets error.message to a "Progress conflict" default when the
      //      server omits it, which would shadow the informative English
      //      fallback below.
      const prevDevice = result.error?.data?.data?.previous_device;
      const model = prevDevice?.device_model || "another device";
      const rawServerMsg = result.error?.data?.message;
      this._error = rawServerMsg
        || `You have unsynced progress on ${model}. Please open that device to sync first, or contact support to switch devices.`;
    } else {
      this._error = t("login.errorGeneric");
    }
    this._scheduleRender();
  };

  render() {
    return html`
      <img class="backdrop" alt=""
           src="${ASSET_BASE}/splash-bg@2x.png"
           srcset="${ASSET_BASE}/splash-bg@2x.png 2x, ${ASSET_BASE}/splash-bg@3x.png 3x" />
      <div class="screen">
        <form class="stack" on:submit=${this._onSubmit}>
          <input id="login-username" name="username" type="text" autocomplete="username"
                 placeholder="${t("login.username")}" aria-label="${t("login.username")}"
                 .value=${this._username} .disabled=${this._submitting}
                 on:input=${this._onUsernameInput} />
          <input id="login-password" name="password" type="password" autocomplete="current-password"
                 placeholder="${t("login.password")}" aria-label="${t("login.password")}"
                 .value=${this._password} .disabled=${this._submitting}
                 on:input=${this._onPasswordInput} />
          <button class="primary" type="submit" .disabled=${this._submitting}>${t("login.submit")}</button>
          <a class="forgot" href="/forgot-password" on:click=${this._onForgotPassword}>${t("login.forgot")}</a>
          ${this._sessionExpired ? html`<p class="message notice" role="status">${t("login.sessionExpired")}</p>` : ""}
          ${this._error ? html`<p class="message error">${this._error}${this._showBuyLink ? html`<br /><a class="buy-link" href="${BUY_URL}" target="_blank" rel="noopener noreferrer">${t("login.buy")}</a>` : ""}</p>` : ""}
        </form>
        <nav class="bottom">
          <a href="${BUY_URL}" target="_blank" rel="noopener noreferrer">${t("login.buy")}</a>
          <a href="${LEARN_MORE_URL}" target="_blank" rel="noopener noreferrer">${t("login.learnMore")}</a>
        </nav>
      </div>
    `;
  }
}
